/**
 * Quick Sale cart helpers: request payloads for cart lines, the client-side
 * discount preview (the cart can be edited before the transaction is synced),
 * and the adapters between cart lines and the shared ApplyDiscountModal.
 */
import {
  ApplyPresetLineItemDiscountInput,
  LineDiscountEntry,
} from "../types/discounts";
import { QuickSaleProduct, QuickSaleProductState } from "../types/quickSale";
import {
  EditTransactionProductRequest,
  GetTransactionsResponse,
  Product,
} from "../types/transactions";
import { calculateStackedDiscount } from "./discountUtils";
import { formatDiscountLabel } from "./discountFormatting";
import {
  DiscountableLine,
  getLineDiscountEntries,
  isSameDiscountSet,
  toLineItemDiscountInputs,
} from "./lineItemDiscounts";

/** A cart line's pre-discount total. */
export const cartLineSubtotal = (product: QuickSaleProductState): number =>
  product.unitPrice * product.quantity;

/**
 * Dollars discounted off a cart line, with the backend math: every % on the
 * pre-discount subtotal, fixed amounts once, capped at the line, floored at 0.
 */
export const cartLineDiscountDollars = (
  product: QuickSaleProductState,
): number =>
  calculateStackedDiscount(cartLineSubtotal(product), product.discounts);

/** True when the cart line carries any discount. */
export const cartLineHasDiscount = (product: QuickSaleProductState): boolean =>
  product.discounts.length > 0;

/** One chip label per discount on a cart line ("Birthday 20%"). */
export const cartLineDiscountLabels = (
  product: QuickSaleProductState,
): string[] =>
  product.discounts.map((d) =>
    formatDiscountLabel(d.name, d.discount_type, d.amount),
  );

/** Total dollars discounted across the cart (summary preview). */
export const calculateCartDiscountTotal = (
  products: QuickSaleProductState[],
): number =>
  products.reduce((sum, product) => sum + cartLineDiscountDollars(product), 0);

/** Staged / applied cart discounts as create-quick-sale preset inputs. */
export const toQuickSaleDiscountInputs = (
  discounts: LineDiscountEntry[],
): ApplyPresetLineItemDiscountInput[] =>
  discounts.flatMap((d) =>
    d.discount_preset_id
      ? [
          {
            discount_preset_id: d.discount_preset_id,
            discount_type: d.discount_type,
            amount: d.amount,
          },
        ]
      : [],
  );

/**
 * The create-quick-sale / add-line-items product payload for a cart line.
 * `includeDiscounts: false` leaves its discounts off (they're applied
 * afterwards with a line-items PUT).
 */
export function buildQuickSaleProductPayload(
  product: QuickSaleProductState,
  { includeDiscounts = true }: { includeDiscounts?: boolean } = {},
): QuickSaleProduct {
  const discounts = includeDiscounts
    ? toQuickSaleDiscountInputs(product.discounts)
    : [];
  return {
    product_uuid: product.productId,
    quantity: product.quantity,
    ...(discounts.length > 0 && { discounts }),
    ...(product.staffCommissionTarget !== undefined && {
      staff_commission_target: product.staffCommissionTarget,
    }),
  };
}

/** A cart line as an ApplyDiscountModal line (keyed by product id). */
export const cartProductToDiscountableLine = (
  product: QuickSaleProductState,
): DiscountableLine => ({
  key: product.productId,
  kind: "product",
  name: product.productName,
  base: cartLineSubtotal(product),
  discounts: product.discounts,
});

/** The persisted transaction line for a cart product (matched by product id). */
const findTransactionLine = (
  transaction: GetTransactionsResponse,
  productId: string,
): Product | undefined =>
  transaction.line_items.products.find((line) => line.id === productId);

/**
 * PUT /transactions/line-items product edits that bring the persisted quick
 * sale in line with the cart: quantity, commission target and
 * the full discount set, only for lines that differ. Cart lines not on the
 * transaction yet are skipped.
 */
export function buildCartProductEdits(
  products: QuickSaleProductState[],
  transaction: GetTransactionsResponse,
): EditTransactionProductRequest[] {
  return products.flatMap((product) => {
    const line = findTransactionLine(transaction, product.productId);
    if (!line) return [];
    const edit: EditTransactionProductRequest = {
      id: line.id,
      line_item_id: line.line_item_id,
    };
    if (line.quantity !== product.quantity) edit.quantity = product.quantity;
    if (
      product.staffCommissionTarget !== undefined &&
      (line.staff_commission_target ?? null) !== product.staffCommissionTarget
    ) {
      edit.staff_commission_target = product.staffCommissionTarget;
    }
    const persisted = getLineDiscountEntries(line);
    if (!isSameDiscountSet(persisted, product.discounts)) {
      edit.discounts = toLineItemDiscountInputs(product.discounts, persisted);
    }
    const changed =
      edit.quantity !== undefined ||
      edit.staff_commission_target !== undefined ||
      edit.discounts !== undefined;
    return changed ? [edit] : [];
  });
}

/**
 * Refresh each cart line's discounts from the persisted transaction, so staged
 * entries pick up their applied-discount ids after a sync.
 */
export const syncCartDiscountsFromTransaction = (
  products: QuickSaleProductState[],
  transaction: GetTransactionsResponse,
): QuickSaleProductState[] =>
  products.map((product) => {
    const line = findTransactionLine(transaction, product.productId);
    return line
      ? { ...product, discounts: getLineDiscountEntries(line) }
      : product;
  });
